import * as React from "react";
import { gsap } from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { CustomWiggle } from "gsap/CustomWiggle";
import { cn } from "@/lib/utils";

gsap.registerPlugin(CustomEase, CustomWiggle);

const WIGGLE_EASE_NAME = "wavyTextWiggle";
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

export type ShinyWavyTextProps = {
  text: string;
  className?: string;
  duration?: number;
  amplitude?: number;
  opacity?: number;
  stagger?: number;
  repeat?: number;
  wiggles?: number;
  wiggleType?: "easeOut" | "easeInOut";
  dy?: number;
};

export function ShinyWavyText({
  text,
  className,
  duration = 1.5,
  amplitude = 4,
  opacity = 1,
  stagger = 0.04,
  repeat = -1,
  wiggles = 1,
  wiggleType = "easeInOut",
  dy = 3,
}: ShinyWavyTextProps) {
  const containerRef = React.useRef<HTMLParagraphElement>(null);
  const charRefs = React.useRef<HTMLSpanElement[]>([]);
  const easeName = React.useId() ?? WIGGLE_EASE_NAME;
  const characterEntries = React.useMemo(() => {
    const counts: Record<string, number> = {};

    return Array.from(text).map((character) => {
      const keyBase = character === " " ? "space" : character;
      counts[keyBase] = (counts[keyBase] ?? 0) + 1;

      return { character, key: `${keyBase}-${counts[keyBase]}` };
    });
  }, [text]);

  charRefs.current = new Array(characterEntries.length);

  useIsomorphicLayoutEffect(() => {
    const targets = charRefs.current.filter(Boolean);
    if (!containerRef.current || targets.length === 0) {
      return;
    }

    CustomWiggle.create(easeName, {
      wiggles,
      type: wiggleType,
    });

    const ctx = gsap.context(() => {
      gsap.timeline({ repeat }).to(targets, {
        duration,
        y: amplitude,
        opacity,
        ease: easeName,
        stagger,
      });
    }, containerRef);

    return () => {
      ctx.revert();
    };
  }, [
    amplitude,
    duration,
    opacity,
    repeat,
    stagger,
    wiggles,
    wiggleType,
    text,
    easeName,
  ]);

  return (
    <span ref={containerRef} className={cn("relative mx-auto", className)}>
      {characterEntries.map(({ character, key }, index) => {
        const displayCharacter = character === " " ? "\u00A0" : character;

        return (
          <span
            key={key}
            ref={(node) => {
              if (node) {
                charRefs.current[index] = node;
              }
            }}
            className="shiny-text inline-block"
          >
            <span className="shiny-text-text shine">{displayCharacter}</span>
            <svg
              className="shiny-text-svg"
              aria-hidden="true"
              focusable="false"
            >
              <text
                className="shiny-text-svg-text--depth"
                x="0"
                y="0"
                textAnchor="start"
                dominantBaseline="text-before-edge"
                alignmentBaseline="text-before-edge"
                vectorEffect="non-scaling-stroke"
                dy={dy}
                xmlSpace="preserve"
              >
                {displayCharacter}
              </text>
              <text
                className="shiny-text-svg-text--outline"
                x="0"
                y="0.5"
                textAnchor="start"
                dominantBaseline="text-before-edge"
                alignmentBaseline="text-before-edge"
                vectorEffect="non-scaling-stroke"
                xmlSpace="preserve"
              >
                {displayCharacter}
              </text>
            </svg>
          </span>
        );
      })}
    </span>
  );
}
